/**
 * Pure utility to resolve tooth visual state from event history.
 * No side effects, no state, no DOM usage.
 *
 * This is the SINGLE SEMANTIC AUTHORITY for tooth visual state.
 * All tooth image rendering should derive from this function.
 *
 * Canonical tooth visual states (all resolver outputs are exactly one of these):
 *   normal, filling, rct_open, rct_done, crown_cutting, crown, rct_cc, rct_crown, missing, implant
 *
 * Precedence (highest wins when combining events):
 *   1. missing
 *   2. implant
 *   3. rct_crown
 *   4. rct_cc          (RCT crown cutting)
 *   5. crown
 *   6. rct_done
 *   7. rct_open
 *   8. filling
 *   9. normal
 */

import type { ToothEvent } from "../types/domain";
import { mapProcedureLabelToCanonicalVisual } from "./toothVisualMapping";
import {
  getVisualStateFromProcedureStep,
  STEP_TO_SEQUENCE_INDEX,
  getProcedureSequence,
} from "../domain/tooth/procedureSequences";

/**
 * Resolves which image key to use for a tooth based on its event history.
 * Returns one of the canonical visual state keys only.
 *
 * @param toothCode - ISO tooth code (e.g. "UR6", "LL1")
 * @param events - Array of ToothEvent objects for this tooth, ordered chronologically
 * @returns Image key: one of "normal" | "filling" | "rct_open" | "rct_done" | "crown_cutting" | "crown" | "rct_cc" | "rct_crown" | "missing" | "implant"
 */
export function resolveToothImageKey(
  toothCode: string,
  events: ToothEvent[]
): string {
  if (!toothCode || !events || events.length === 0) {
    return "normal";
  }

  // Filter events for this specific tooth
  const toothEvents = events.filter(
    (e) => e.tooth_code === toothCode || (e as { tooth?: string }).tooth === toothCode
  );

  if (toothEvents.length === 0) {
    return "normal";
  }

  // Process events in chronological order
  // Most recent non-undo event wins (but precedence rules apply)
  let finalState: string | null = null;

  for (const event of toothEvents) {
    // Early exit: Skip undo events
    // Undo events revert prior state, so we ignore them and let prior state stand
    const payload = event.payload as { undo?: boolean; symbol?: string } | null;
    if (payload?.undo === true) {
      continue;
    }

    // Map event to visual state (returns null if event doesn't affect visual state)
    const state = mapEventToState(event);
    if (state) {
      // Apply precedence: higher priority states override lower ones
      finalState = applyPrecedence(finalState, state);
    }
  }

  // Conservative fallback
  return finalState || "normal";
}

/**
 * Applies precedence rules: higher priority states override lower ones.
 * All keys must be canonical visual states; unknown keys are treated as lowest priority.
 *
 * Precedence (highest wins):
 *   missing > implant > rct_crown > rct_cc > crown > rct_done > rct_open > filling > normal
 */
function applyPrecedence(current: string | null, candidate: string): string {
  if (!current) return candidate;

  const precedence: Record<string, number> = {
    missing: 9,
    implant: 8,
    rct_crown: 7,
    rct_cc: 6,
    crown: 5,
    rct_done: 4,
    rct_open: 3,
    filling: 2,
    normal: 1,
  };

  const currentPriority = precedence[current] ?? 0;
  const candidatePriority = precedence[candidate] ?? 0;

  return candidatePriority > currentPriority ? candidate : current;
}

/**
 * Procedure symbol → canonical visual state (ToothChart-compatible).
 * Return values must be: normal, filling, rct_open, rct_done, crown_cutting, crown, rct_cc, rct_crown, missing, implant.
 */
const PROCEDURE_SYMBOL_TO_STATE: Record<string, string> = {
  FILLING: "filling",
  RCT_OPEN: "rct_open",
  RCT_DONE: "rct_done",
  CROWN: "crown",
  CROWN_PLACED: "crown",
  CROWN_CUTTING: "crown_cutting",
  RCT_CC: "rct_cc",
  RCT_CROWN: "rct_crown",
  EXTRACTION: "missing",
  EXTRACT: "missing",
  IMPLANT_PLACED: "implant",
  IMPLANT_RESTORED: "crown",
};

/** PascalCase (sequence) state → canonical visual state for resolver output. */
const PASCAL_TO_CANONICAL: Record<string, string> = {
  Normal: "normal",
  Filling: "filling",
  RCT_Open: "rct_open",
  RCT_Done: "rct_done",
  Crown_Cutting: "crown_cutting",
  Crown: "crown",
  RCT_CC: "rct_cc",
  RCT_Crown: "rct_crown",
  Missing: "missing",
  Implant: "implant",
};

/**
 * Maps a single ToothEvent to a visual state string.
 * Returns null if event doesn't affect visual state.
 * Outputs only canonical states compatible with ToothChart.
 * Undo events are ignored by the caller.
 * Uses procedure sequence (procedure_type + step) when available; otherwise symbol/event_type.
 */
function mapEventToState(event: ToothEvent): string | null {
  const payload = event.payload as {
    symbol?: string;
    multi_tooth?: string | null;
    procedure_type?: string;
    step_label?: string;
    diagnosis?: string;
    procedure_master_id?: string;
    step_key?: string;
    step?: string;
    step_no?: number;
  } | null;
  const eventWithSymbol = event as ToothEvent & { symbol?: string; step_key?: string };
  const symbol = eventWithSymbol.symbol || payload?.symbol || "";
  const eventType = event.event_type?.toLowerCase() || "";

  // Procedure symbol → canonical state (explicit mapping)
  const upper = symbol.toUpperCase();
  if (PROCEDURE_SYMBOL_TO_STATE[upper] !== undefined) {
    return PROCEDURE_SYMBOL_TO_STATE[upper];
  }
  if (PROCEDURE_SYMBOL_TO_STATE[symbol] !== undefined) {
    return PROCEDURE_SYMBOL_TO_STATE[symbol];
  }

  // Master/procedure text fallback: allow semantic mapping from labels into canonical states.
  const textCandidates = [
    payload?.step_label,
    payload?.diagnosis,
    payload?.procedure_type,
    payload?.procedure_master_id,
    symbol,
  ].filter(Boolean) as string[];
  for (const text of textCandidates) {
    const mapped = mapProcedureLabelToCanonicalVisual(text);
    if (mapped && mapped !== "normal") {
      return mapped;
    }
  }

  // Sequence resolver: procedure_type + step_key → sequence index → visual state
  const procedureType =
    payload?.procedure_type ??
    (payload?.procedure_master_id as string | undefined) ??
    "";
  const stepKey =
    payload?.step_key ??
    payload?.step ??
    eventWithSymbol.step_key ??
    "";
  const stepNo = payload?.step_no;
  if (procedureType && (stepKey in STEP_TO_SEQUENCE_INDEX || typeof stepNo === "number")) {
    const stepForSequence =
      stepKey in STEP_TO_SEQUENCE_INDEX
        ? stepKey
        : stepNo != null && stepNo >= 1
          ? Object.entries(STEP_TO_SEQUENCE_INDEX).find(([, i]) => i === stepNo)?.[0] ?? ""
          : "";
    if (stepForSequence) {
      const pascalState = getVisualStateFromProcedureStep(procedureType, stepForSequence);
      const canonical = PASCAL_TO_CANONICAL[pascalState] ?? pascalState.toLowerCase();
      if (canonical) return canonical;
    }
    // step_no only: 1-based step_no → sequence index (1 = first step state, e.g. RCT_Open)
    if (typeof stepNo === "number" && stepNo >= 1) {
      const seq = getProcedureSequence(procedureType);
      const idx = Math.min(stepNo, seq.length - 1);
      const pascalState = seq[idx] ?? seq[seq.length - 1] ?? "Normal";
      const canonical = PASCAL_TO_CANONICAL[pascalState];
      if (canonical) return canonical;
    }
  }

  // Legacy event_type fallbacks (same canonical outputs)
  if (eventType === "extraction") return "missing";
  if (eventType === "implant_placed") return "implant";
  if (eventType === "implant_restored") return "crown";
  if (eventType === "rct_crown") return "rct_crown";
  if (eventType === "rct_cc") return "rct_cc";
  if (eventType === "rct_done") return "rct_done";
  if (eventType === "rct_open") return "rct_open";
  if (eventType === "crown_cutting") return "crown_cutting";
  if (eventType === "crown" || eventType === "crown_placed") return "crown";
  if (eventType === "filling") return "filling";
  if (eventType === "reimplanted") return "normal";

  // Non-visual: mark/unmark don't change tooth image
  if (eventType === "mark" || eventType === "unmark") {
    return null;
  }

  return null;
}
